import { cn } from "@/lib/utils";

interface WordmarkProps {
  className?: string;
  /** Tailwind font-size class, e.g. "text-xl", "text-3xl" */
  size?: string;
  /**
   * Use the italic cut of the display font when available. Arapey's
   * italic is its standout feature; on the homepage / hero we turn it
   * on, but in the header / footer we stick with roman so the logo sits
   * level next to the circular mark.
   */
  italic?: boolean;
}

/**
 * Whistll wordmark. Displayed in the brand display font defined via
 * `theme.extend.fontFamily.script` in tailwind.config.js (currently
 * Cormorant Garamond, an Italian-renaissance serif via Google
 * Fonts with real 300-700 weights in roman + italic - close cousin
 * of Arapey).
 *
 * The clever bit: we replace the lowercase 'i' with a stacked
 * composition - a dotless 'ı' glyph as the visible stem plus a volt-
 * coloured circle as the dot - so the single orange pixel doubles as
 * the brand accent. Swap the font family above and this trick keeps
 * working as long as the new family supports sentence case.
 */
export function Wordmark({
  className,
  size = "text-xl",
  italic = false,
}: WordmarkProps) {
  return (
    <span
      className={cn(
        "inline-flex items-baseline font-script font-bold leading-none tracking-tight",
        italic && "italic",
        size,
        className
      )}
    >
      Wh
      <span className="relative inline-block">
        {/* Invisible 'i' reserves the correct glyph width so the
            stacked replacement doesn't shift the following letters. */}
        <span aria-hidden className="opacity-0">
          i
        </span>
        {/* Dotless 'ı' (U+0131) is the visible stem, centred over the
            reserved width. */}
        <span
          aria-hidden
          className="absolute inset-0 flex items-center justify-center"
        >
          ı
        </span>
        {/* Volt dot. Sized relative to the font so it scales with the
            wordmark. Positioned above where the 'i' dot would sit. */}
        <span
          aria-hidden
          className="absolute left-1/2 top-[14%] h-[0.18em] w-[0.18em] -translate-x-1/2 rounded-full bg-volt"
        />
      </span>
      stll
    </span>
  );
}
